import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { notificationService, Notification } from '../services/notificationService';
import styles from '../styles/modules/AnnouncementModal.module.css';

interface AnnouncementModalProps {
  onNotificationRead?: () => void;
}

const AnnouncementModal: React.FC<AnnouncementModalProps> = ({ onNotificationRead }) => {
  const { t } = useTranslation();
  const [announcements, setAnnouncements] = useState<Notification[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [markingRead, setMarkingRead] = useState(false);

  const fetchAnnouncements = useCallback(async () => {
    try {
      setLoading(true);
      const response = await notificationService.getMyNotifications();
      
      // Filter for UNREAD announcements only
      const announcementList = response.notifications.filter(
        (notif: Notification) => notif.category === 'announcement' && !notif.isRead
      );
      
      setAnnouncements(announcementList);
      if (announcementList.length > 0) {
        setShowModal(true);
      }
    } catch (error) {
      console.error('Error fetching announcements:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAnnouncements();
  }, [fetchAnnouncements]);

  const handleNext = () => {
    if (currentIndex < announcements.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleMarkAsRead = async () => {
    const currentAnnouncement = announcements[currentIndex];
    if (!currentAnnouncement || markingRead) return;

    setMarkingRead(true);
    try {
      await notificationService.markAsRead(currentAnnouncement._id);
      const updated = announcements.filter((a) => a._id !== currentAnnouncement._id);
      setAnnouncements(updated);

      if (updated.length === 0) {
        setShowModal(false);
        setCurrentIndex(0);
      } else if (currentIndex >= updated.length) {
        setCurrentIndex(updated.length - 1);
      }
      
      // Wait a bit before notifying parent to allow backend to commit
      if (onNotificationRead) {
        setTimeout(() => {
          onNotificationRead();
        }, 300);
      }
    } catch (error) {
      console.error('Error marking announcement as read:', error);
    } finally {
      setMarkingRead(false);
    }
  };

  if (!showModal || announcements.length === 0 || loading) {
    return null;
  }

  const currentAnnouncement = announcements[currentIndex];

  return (
    <div className={styles.announcementOverlay} onClick={() => setShowModal(false)}>
      <div className={styles.announcementModal} onClick={(e) => e.stopPropagation()}>
        {/* Close Button */}
        <button
          className={styles.announcementClose}
          onClick={() => setShowModal(false)}
          title={t('common.close')}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>

        {/* Content */}
        <div className={styles.announcementContent}>
          <div className={styles.announcementDate}>
            {new Date(currentAnnouncement.createdAt).toLocaleDateString(undefined, {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })}
          </div>
          <h2 className={styles.announcementTitle}>{currentAnnouncement.title}</h2>
          <div className={styles.announcementBody}>{currentAnnouncement.content}</div>
        </div>

        {/* Navigation */}
        {announcements.length > 1 && (
          <>
            <div className={styles.announcementCounter}>
              {currentIndex + 1} of {announcements.length}
            </div>
            <div className={styles.announcementNavigation}>
              <button
                className={styles.btnNav}
                onClick={handlePrev}
                disabled={currentIndex === 0}
                title={t('notifications.previousAnnouncement')}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="15 18 9 12 15 6"/>
                </svg>
              </button>
              <button
                className={styles.btnNav}
                onClick={handleNext}
                disabled={currentIndex === announcements.length - 1}
                title={t('notifications.nextAnnouncement')}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="9 18 15 12 9 6"/>
                </svg>
              </button>
            </div>
          </>
        )}

        <div className={styles.announcementActions}>
          <button
            className={styles.btnAcknowledge}
            onClick={handleMarkAsRead}
            disabled={markingRead}
            title={t('notifications.markAsRead')}
            aria-label={t('notifications.markAsRead')}
          >
            {markingRead ? (
              t('common.loading')
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AnnouncementModal;
